import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { Flex } from "@magnetic/flex";
import { Heading } from "@magnetic/heading";
import { Input } from "@magnetic/input";
import { Link } from "@magnetic/link";
import { Text } from "@magnetic/text";

import { CiscoLogo } from "src/common/ciscoLogo";
import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { VFlex } from "src/components/vFlex";
import { ThemeContext } from "src/core/themeContext";
import { useUrlState } from "src/hooks/useUrlState";
import { GlobalFooter } from "src/layouts/globalFooter";
import { Constants } from "src/utils/constants";

import { isEmail, PROD_NEED_HELP, PROD_SIGN_UP } from "./constants";

import "./login.scss";

interface LoginViewProps {
  readonly signUpLink: string;
  readonly needHelpLink: string;
  readonly children?: React.ReactNode;
  readonly initialError?: string;
  readonly startUrl?: string;
}

export const LoginView = ({
  signUpLink,
  needHelpLink,
  children,
  initialError,
  startUrl: startUrlProp
}: LoginViewProps) => {
  const location = useLocation();
  const [error, setError] = useState<string | undefined>(initialError);
  const formRef = useRef<HTMLFormElement>(null);
  const { theme, applyTheme } = useContext(ThemeContext);
  const [receivedError, setReceivedError] = useUrlState("error", "", true);

  const signInUsingCredentials =
    "Sign in with cisco.com or Cisco Unified Identity credentials";

  const buttonText = "Continue";

  const dontHaveLogin = "Don't have an account?";

  /**
   * Using the serialized Location object passed from the shell, rebuild the destination as relative URL
   */
  function buildDestinationLocation(loc: Location) {
    return [window.location.origin, loc.pathname, loc.search, loc.hash].join(
      ""
    );
  }

  /**
   * Extract the tenantId from the URL
   */
  function getTenantIdFromURL(loc: Location) {
    return new URLSearchParams(loc.search).get("tenantId") || "";
  }

  /**
   * Extract the email from the URL
   */
  function getEmailFromURL(loc: Location) {
    return new URLSearchParams(loc.search).get("email") || "";
  }

  /**
   * Extract the post-login destination from the URL.
   */
  function getStartUrlFromURL(loc: Location, currentOrigin: string) {
    const startUrl = new URLSearchParams(loc.search).get("startUrl") || "";
    if (!startUrl) {
      return "";
    }
    try {
      const parsed = new URL(startUrl, currentOrigin);
      return parsed.origin === currentOrigin ? parsed.toString() : "";
    } catch {
      return "";
    }
  }

  /**
   * Initially, due to asynchronous timing of waiting for the `state` property to resolve, we only have a standard `location`
   * to read from the browser's URL. In a few event turns, the additional `NavigationOptions` will resolve
   */
  const getNavigationLocation = useMemo(() => {
    return location.state ? location.state.location : location;
  }, [location]);

  const formStartUrl = useMemo(() => {
    if (startUrlProp) {
      return `${window.location.origin}${startUrlProp}`;
    }
    return (
      getStartUrlFromURL(getNavigationLocation, window.location.origin) ||
      buildDestinationLocation(getNavigationLocation)
    );
  }, [getNavigationLocation, startUrlProp]);

  useEffect(() => {
    applyTheme(theme);
  }, [applyTheme, theme]);

  // Initialize error from backend-provided query param (e.g. /login?error=...) for all LoginView usages.
  useEffect(() => {
    if (receivedError) {
      // Prefer an explicit initialError prop if provided, otherwise use URL error.
      setError((prev) => prev ?? receivedError);

      // After reading the error from the URL, remove it so it doesn't persist on refresh/back navigation.
      setReceivedError("");
    }
  }, [receivedError, setReceivedError]);

  /**
   * Moved to using form submit to handle
   * the case where the user hits enter instead
   * of clicking the "sign in" button. Using
   * formEvent.preventDefault(); should keep this
   * from causing problems for QA automation
   *
   * Connect with third-party OKTA IdP for user authentication flow
   */
  const onFormSubmit = useCallback(
    (formEvent: React.FormEvent<HTMLFormElement>) => {
      formEvent.preventDefault();
      const data = new FormData(formEvent.currentTarget);
      const email = data.get("email");
      if (email === "") {
        setError("Enter email address to continue");
        return;
      }
      if (!isEmail(email)) {
        setError("Enter a valid email address to continue");
        return;
      }

      const inputForm = formEvent.target as HTMLFormElement;
      inputForm.action = "/ui/v1/sessions";
      inputForm.method = "post";
      inputForm.submit();
    },
    []
  );

  useEffect(() => {
    const email = getEmailFromURL(getNavigationLocation);
    if (email && isEmail(email) && formRef.current) {
      const event = {
        preventDefault: () => {},
        currentTarget: formRef.current,
        target: formRef.current
      } as unknown as React.FormEvent<HTMLFormElement>;
      onFormSubmit(event);
    }
  }, [getNavigationLocation, onFormSubmit]);

  return (
    <div className="login-page">
      <div className="login">
        <CiscoLogo />
        <form ref={formRef} onSubmit={onFormSubmit}>
          <VFlex gap="md">
            <Flex align="center" justify="center">
              <Heading size="section" className="brand">
                {Constants.APP_NAME} Sign In
              </Heading>
            </Flex>
            <Text size="p3" className="text" data-qa="title-subtext">
              {signInUsingCredentials}
            </Text>
          </VFlex>
          <Input
            label="Email"
            name="email"
            type="email"
            size="md"
            required={true}
            fixedWidth={true}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
            defaultValue={getEmailFromURL(getNavigationLocation)}
          />
          <input type="hidden" name={"startUrl"} value={formStartUrl} />
          {(() => {
            const tenantId = getTenantIdFromURL(getNavigationLocation);
            return (
              tenantId && (
                <input type="hidden" name={"tenantId"} value={tenantId} />
              )
            );
          })()}
          {children}
          <ErrorNotification msg={error} />
          <Button type="submit" size="md" name="signin" data-qa="button-login">
            {buttonText}
          </Button>
          <VFlex gap="xs" className="help-text">
            <Text size="p3" data-qa="signup-text">
              {dontHaveLogin}&nbsp;
              <Link href={signUpLink} className="login-link">
                Sign up here.
              </Link>
            </Text>
            <Text size="p3">
              <Link href={needHelpLink} className="login-link">
                Need help?
              </Link>
            </Text>
          </VFlex>
        </form>
        <GlobalFooter showCopyright={false} textSize="p3" />
      </div>
    </div>
  );
};

export const Login = () => {
  return <LoginView signUpLink={PROD_SIGN_UP} needHelpLink={PROD_NEED_HELP} />;
};
